<template>
  <div class="card-body">
    <LoadingContainer v-if="loading" />

    <EmptyState
      v-else-if="!connectAccounts || connectAccounts.length === 0"
      :msg="'Nenhuma conta Stripe Connect encontrada'"
      :title="'Sem contas'"
    />

    <div v-else>
      <div class="mb-3 text-end">
        <button
          class="btn btn-outline-danger btn-sm"
          @click="showResetModal = true"
        >
          <i class="fa-solid fa-rotate-left me-1"></i> Resetar conta Stripe
        </button>
      </div>

      <div class="row">
        <div class="col-12">
          <div
            v-for="(account, index) in sortedConnectAccounts"
            :key="'connect-' + index"
            class="card mb-3 connect-card"
            :class="{
              'connect-card-active-healthy': isActiveAccount(account) && isAccountHealthy(account) && pendingLevel(account) === 'none',
              'connect-card-active-future': isActiveAccount(account) && isAccountHealthy(account) && pendingLevel(account) === 'future',
              'connect-card-active-warning': isActiveAccount(account) && !isAccountHealthy(account),
            }"
          >
            <!-- Header -->
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center gap-2">
                <span class="badge bg-info">{{ getBusinessTypeLabel(account.business_type) }}</span>
                <span
                  v-if="isActiveAccount(account)"
                  class="badge"
                  :class="activeBadgeClass(account)"
                >
                  <i class="me-1" :class="activeBadgeIcon(account)"></i>
                  {{ activeBadgeText(account) }}
                </span>
                <span
                  v-if="account.reset_at"
                  class="badge bg-secondary"
                  :title="`Resetada em ${formatDateTime(account.reset_at)}`"
                >
                  <i class="fa-solid fa-ban me-1"></i>Inativa
                </span>
                <small class="text-muted">{{ account.stripe_id }}</small>
              </div>
              <a
                v-if="account.stripe_id && account.stripe_source_account !== 'old'"
                :href="getStripeLink(account.stripe_id)"
                target="_blank"
                class="btn btn-sm btn-outline-primary"
              >
                <i class="fa-brands fa-stripe"></i> Ver no Stripe
              </a>
            </div>

            <div class="card-body">
              <!-- Aviso conta antiga -->
              <div v-if="account.stripe_source_account === 'old'" class="old-account-banner mb-3">
                <div class="old-account-icon">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div class="old-account-content">
                  <div class="old-account-title">
                    Conta Stripe Connect Antiga (Desativada)
                  </div>
                  <p class="mb-1">
                    Esta é uma conta Connect da nossa <strong>conta Stripe antiga</strong>,
                    em nome da empresa <strong>Tudo Amor Proprio Ltda</strong>.
                  </p>
                  <p class="mb-0">
                    <strong>Esta conta foi desativada</strong> e não deve mais ser utilizada para
                    novos repasses ou cobranças.
                  </p>
                </div>
              </div>

              <!-- Grid: Dados + Status -->
              <div class="row g-3">
                <!-- Coluna: Dados da Conta -->
                <div class="col-md-6">
                  <div class="info-section">
                    <h6 class="section-title">
                      <i class="fa-solid fa-user me-2"></i>Dados da Conta
                    </h6>
                    <table class="info-table">
                      <tr>
                        <td class="label">Tipo</td>
                        <td>{{ getBusinessTypeLabel(account.business_type) }}</td>
                      </tr>
                      <tr>
                        <td class="label">Nome</td>
                        <td><strong>{{ getAccountName(account) }}</strong></td>
                      </tr>
                      <tr v-if="account.business_type === 'company' && account.company_document_number">
                        <td class="label">CNPJ</td>
                        <td>{{ account.company_document_number }}</td>
                      </tr>
                      <tr>
                        <td class="label">Criado em</td>
                        <td>{{ account.created_at_formatted }}</td>
                      </tr>
                    </table>
                  </div>
                </div>

                <!-- Coluna: Status -->
                <div class="col-md-6">
                  <div class="info-section">
                    <h6 class="section-title">
                      <i class="fa-solid fa-circle-check me-2"></i>Status
                    </h6>
                    <table class="info-table">
                      <tr>
                        <td class="label">Cobranças</td>
                        <td>
                          <span :class="account.charges_enabled ? 'status-ok' : 'status-off'">
                            <i :class="account.charges_enabled ? 'fa-solid fa-check' : 'fa-solid fa-xmark'"></i>
                            {{ account.charges_enabled ? "Habilitado" : "Desabilitado" }}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="label">Repasses</td>
                        <td>
                          <span :class="account.payouts_enabled ? 'status-ok' : 'status-off'">
                            <i :class="account.payouts_enabled ? 'fa-solid fa-check' : 'fa-solid fa-xmark'"></i>
                            {{ account.payouts_enabled ? "Habilitado" : "Desabilitado" }}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="label">Pendências</td>
                        <td>
                          <span v-if="account.currently_due && account.currently_due.length > 0" class="badge bg-warning text-dark">
                            {{ account.currently_due.length }} item(s)
                          </span>
                          <span v-else-if="hasFuturePending(account)" class="badge bg-info text-dark">
                            {{ account.futureCurrentlyDue.length }} futura(s)
                          </span>
                          <span v-else class="status-ok">
                            <i class="fa-solid fa-check"></i> Nenhuma
                          </span>
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Saldo -->
              <div
                v-if="account.stripe_balance_available_amount !== undefined || account.stripe_balance_pending_amount !== undefined"
                class="info-section mt-3"
              >
                <h6 class="section-title">
                  <i class="fa-solid fa-wallet me-2"></i>Saldo
                </h6>
                <div class="d-flex flex-wrap gap-4 align-items-center">
                  <div v-if="account.stripe_balance_available_amount !== undefined">
                    <small class="text-muted d-block">Disponível</small>
                    <strong :class="getBalanceClass(account.stripe_balance_available_amount)" class="fs-5">{{ formatCurrency(account.stripe_balance_available_amount) }}</strong>
                  </div>
                  <div v-if="account.stripe_balance_pending_amount !== undefined">
                    <small class="text-muted d-block">Pendente</small>
                    <span :class="getBalanceClass(account.stripe_balance_pending_amount)" class="fs-5">{{ formatCurrency(account.stripe_balance_pending_amount) }}</span>
                  </div>
                  <div v-if="account.stripe_balance_updated_at" class="ms-auto text-muted">
                    <small><i class="fa-solid fa-clock me-1"></i>Atualizado: {{ formatDateTime(account.stripe_balance_updated_at) }}</small>
                  </div>
                </div>
              </div>

              <!-- Documentos -->
              <div
                v-if="account.bank_document_front || account.bank_document_back || account.company_document_front"
                class="info-section mt-3"
              >
                <h6 class="section-title">
                  <i class="fa-solid fa-file-lines me-2"></i>Documentos
                </h6>
                <div class="d-flex flex-wrap gap-3">
                  <div v-if="account.bank_document_front" class="doc-item">
                    <small class="text-muted d-block mb-1">Banco (frente)</small>
                    <a :href="getDocumentUrl(account.bank_document_front)" target="_blank" class="doc-link" v-if="isPdf(account.bank_document_front)">
                      <i class="fa-solid fa-file-pdf text-danger fa-2x"></i>
                    </a>
                    <a :href="getDocumentUrl(account.bank_document_front)" target="_blank" v-else>
                      <img :src="getDocumentUrl(account.bank_document_front)" class="doc-preview" />
                    </a>
                  </div>
                  <div v-if="account.bank_document_back" class="doc-item">
                    <small class="text-muted d-block mb-1">Banco (verso)</small>
                    <a :href="getDocumentUrl(account.bank_document_back)" target="_blank" class="doc-link" v-if="isPdf(account.bank_document_back)">
                      <i class="fa-solid fa-file-pdf text-danger fa-2x"></i>
                    </a>
                    <a :href="getDocumentUrl(account.bank_document_back)" target="_blank" v-else>
                      <img :src="getDocumentUrl(account.bank_document_back)" class="doc-preview" />
                    </a>
                  </div>
                  <div v-if="account.company_document_front" class="doc-item">
                    <small class="text-muted d-block mb-1">Empresa</small>
                    <a :href="getDocumentUrl(account.company_document_front)" target="_blank" class="doc-link" v-if="isPdf(account.company_document_front)">
                      <i class="fa-solid fa-file-pdf text-danger fa-2x"></i>
                    </a>
                    <a :href="getDocumentUrl(account.company_document_front)" target="_blank" v-else>
                      <img :src="getDocumentUrl(account.company_document_front)" class="doc-preview" />
                    </a>
                  </div>
                </div>
              </div>

              <!-- Alertas -->
              <div
                v-if="(account.last_payout_failed && account.last_failed_payout) || (account.currently_due && account.currently_due.length > 0) || hasFuturePending(account)"
                class="alerts-section mt-3"
              >
                <!-- Repasse Falhou -->
                <div v-if="account.last_payout_failed && account.last_failed_payout" class="alert-box alert-danger mb-2">
                  <div class="alert-header">
                    <i class="fa-solid fa-circle-xmark"></i>
                    <strong>Último Repasse Falhou</strong>
                    <a
                      v-if="account.last_failed_payout.stripe_payout_id"
                      :href="getPayoutStripeLink(account.stripe_id, account.last_failed_payout.stripe_payout_id)"
                      target="_blank"
                      class="btn btn-sm btn-outline-secondary ms-auto"
                    >
                      <i class="fa-brands fa-stripe me-1"></i> Ver no Stripe
                    </a>
                  </div>
                  <div class="alert-content">
                    <p v-if="account.last_failed_payout.failure_code" class="mb-1">
                      <strong>Motivo:</strong> {{ getFailureMessageTranslated(account.last_failed_payout.failure_code) }}
                    </p>
                    <small class="text-muted">
                      <span v-if="account.last_failed_payout.amount">Valor: {{ formatCurrency(account.last_failed_payout.amount) }}</span>
                      <span v-if="account.last_failed_payout.amount && account.last_failed_payout.created_at"> · </span>
                      <span v-if="account.last_failed_payout.created_at">Data: {{ formatDateTime(account.last_failed_payout.created_at) }}</span>
                    </small>
                  </div>
                </div>

                <!-- Pendências -->
                <div v-if="account.currently_due && account.currently_due.length > 0" class="alert-box alert-warning">
                  <div class="alert-header">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <strong>Pendências</strong>
                    <span class="badge bg-warning text-dark ms-2">{{ account.currently_due.length }}</span>
                  </div>
                  <div class="alert-content">
                    <ul class="pendencias-list">
                      <li v-for="(pendencia, idx) in account.currently_due" :key="'due-' + idx">
                        {{ pendencia }}
                      </li>
                    </ul>
                    <div v-if="account.requiresStripeHostedFlow" class="stripe-hosted-note mt-2">
                      <i class="fa-solid fa-triangle-exclamation me-1"></i>
                      Essa conta precisa de verificação de identidade — o psi será
                      redirecionado para o site da Stripe ao abrir o link.
                    </div>
                    <div v-if="account.pendenciasLink && account.stripe_source_account !== 'old'" class="mt-2 pt-2 border-top">
                      <div class="input-group input-group-sm">
                        <span class="input-group-text"><i class="fa-solid fa-link"></i></span>
                        <input type="text" class="form-control bg-light" :value="account.pendenciasLink" readonly />
                        <button class="btn btn-primary" @click="copyPendenciasLink(account.pendenciasLink)" title="Copiar link">
                          <i class="fa-solid fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Pendências Futuras -->
                <div v-if="hasFuturePending(account)" class="alert-box alert-info mt-2">
                  <div class="alert-header">
                    <i class="fa-solid fa-clock"></i>
                    <strong>Pendências Futuras</strong>
                    <span class="badge bg-info text-dark ms-2">{{ account.futureCurrentlyDue.length }}</span>
                    <span v-if="account.futureDeadlineFormatted" class="text-muted ms-2 small">
                      Prazo: {{ account.futureDeadlineFormatted }}
                    </span>
                  </div>
                  <div class="alert-content">
                    <p class="text-muted small mb-2">
                      Não bloqueiam pagamentos ainda, mas vão bloquear se não forem
                      resolvidas até o prazo.
                    </p>
                    <ul class="pendencias-list">
                      <li v-for="(pendencia, idx) in account.futureCurrentlyDue" :key="'future-due-' + idx">
                        {{ pendencia }}
                      </li>
                    </ul>
                    <div v-if="account.requiresStripeHostedFlow" class="stripe-hosted-note mt-2">
                      <i class="fa-solid fa-triangle-exclamation me-1"></i>
                      Essa conta precisa de verificação de identidade — o psi será
                      redirecionado para o site da Stripe ao abrir o link.
                    </div>
                    <div v-if="account.pendenciasLink && account.stripe_source_account !== 'old'" class="mt-2 pt-2 border-top">
                      <div class="input-group input-group-sm">
                        <span class="input-group-text"><i class="fa-solid fa-link"></i></span>
                        <input type="text" class="form-control bg-light" :value="account.pendenciasLink" readonly />
                        <button class="btn btn-primary" @click="copyPendenciasLink(account.pendenciasLink)" title="Copiar link">
                          <i class="fa-solid fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Reset Stripe -->
    <div
      class="modal fade"
      :class="{ show: showResetModal }"
      :style="{ display: showResetModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title">
              <i class="fa-solid fa-triangle-exclamation me-2"></i>
              Resetar conta Stripe Connect
            </h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              @click="closeResetModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              <strong>Atenção!</strong> Esta ação irá:
            </div>
            <ul class="mb-3">
              <li>A conta do PsiApp do psicólogo <strong>continua normal</strong></li>
              <li>Somente o cadastro na <strong>plataforma de pagamento</strong> (Stripe) será resetado</li>
              <li>O psicólogo será <strong>deslogado</strong> do aplicativo</li>
              <li>Será necessário pedir para ele <strong>logar novamente</strong> com o mesmo email e senha</li>
              <li>Ao logar, ele precisará criar um <strong>novo cadastro</strong> na plataforma de pagamento</li>
            </ul>
            <div class="form-check mt-4">
              <input
                class="form-check-input"
                type="checkbox"
                id="confirmResetCheckbox"
                v-model="confirmReset"
              />
              <label class="form-check-label" for="confirmResetCheckbox">
                <strong>Eu li e entendi</strong> as consequências desta ação
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeResetModal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="!confirmReset || resetting"
              @click="handleResetStripe"
            >
              <span v-if="resetting" class="spinner-border spinner-border-sm me-1"></span>
              Confirmar Reset
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showResetModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
export default {
  name: "PsiDetailConnectAccounts",
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      connectAccounts: [],
      showResetModal: false,
      confirmReset: false,
      resetting: false,
    };
  },
  mounted() {
    this.$eventBus.$on("psiDetail:openResetStripe", this.openResetModal);
  },
  beforeDestroy() {
    this.$eventBus.$off("psiDetail:openResetStripe", this.openResetModal);
  },
  computed: {
    sortedConnectAccounts() {
      if (!this.connectAccounts || this.connectAccounts.length === 0) return [];
      const activeId = this.item?.stripe_id;
      return [...this.connectAccounts].sort((a, b) => {
        const aActive = a.stripe_id === activeId ? 0 : 1;
        const bActive = b.stripe_id === activeId ? 0 : 1;
        return aActive - bActive;
      });
    },
  },
  methods: {
    openResetModal() {
      this.showResetModal = true;
    },
    isActiveAccount(account) {
      return this.item && this.item.stripe_id === account.stripe_id;
    },
    isAccountHealthy(account) {
      const noPendencias = !account.currently_due || account.currently_due.length === 0;
      return !!account.charges_enabled && !!account.payouts_enabled && noPendencias;
    },
    // 'urgent' (bloqueia agora) | 'future' (vai bloquear se não resolver) | 'none'
    pendingLevel(account) {
      if (account.currently_due && account.currently_due.length > 0) return 'urgent';
      if (account.hasAnyPending) return 'future';
      return 'none';
    },
    hasFuturePending(account) {
      return this.pendingLevel(account) === 'future';
    },
    activeBadgeClass(account) {
      if (!this.isAccountHealthy(account)) return 'bg-warning text-dark';
      if (this.pendingLevel(account) === 'future') return 'bg-info text-dark';
      return 'bg-success';
    },
    activeBadgeIcon(account) {
      if (!this.isAccountHealthy(account)) return 'fa-solid fa-triangle-exclamation';
      if (this.pendingLevel(account) === 'future') return 'fa-solid fa-clock';
      return 'fa-solid fa-check';
    },
    activeBadgeText(account) {
      if (!this.isAccountHealthy(account)) return 'Ativa com pendências';
      if (this.pendingLevel(account) === 'future') return 'Ativa — pendência futura';
      return 'Ativa';
    },
    async loadConnectAccounts() {
      if (!this.item?.id) return;

      this.loading = true;
      try {
        const response = await this.$connectAccountService.list({
          user_id: this.item.id,
        });

        this.connectAccounts = response.data.map((account) => {
          let currentlyDue = account.currently_due_ptbr;
          if (typeof currentlyDue === 'string' && currentlyDue.length > 0) {
            currentlyDue = currentlyDue.split('|');
          } else if (!Array.isArray(currentlyDue)) {
            currentlyDue = [];
          }

          return {
            ...account,
            currently_due: currentlyDue,
            pendenciasLink: null,
            fullFormLink: null,
            // null = ainda não sabemos (aguardando pendencias-link); evita "tudo em
            // dia" precipitado quando só há pendência futura (currently_due vazio)
            hasAnyPending: null,
            futureCurrentlyDue: [],
            futureDeadlineFormatted: null,
            requiresStripeHostedFlow: false,
            created_at_formatted: account.created_at
              ? this.$moment(account.created_at).format("DD/MM/YYYY HH:mm")
              : "-",
          };
        });

        // Busca o status completo de pendências (atuais + futuras) de cada conta,
        // exceto contas antigas — currently_due isolado não é suficiente pra saber
        // se a conta está "tudo em dia" (pode ter só pendência futura)
        this.connectAccounts.forEach((acc) => {
          if (acc.stripe_source_account !== 'old') {
            this.fetchPendenciasLinkForAccount(acc);
          }
        });
      } catch (error) {
        console.error("Erro ao carregar contas Stripe Connect:", error);
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao carregar contas Stripe Connect",
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchPendenciasLinkForAccount(account) {
      try {
        const res = await this.$connectAccountService.get(
          `api/connect-accounts/pendencias-link`,
          { connect_account_id: account.id }
        );
        const data = res.data || {};

        let futureCurrentlyDue = data.future_currently_due_ptbr;
        if (typeof futureCurrentlyDue === 'string' && futureCurrentlyDue.length > 0) {
          futureCurrentlyDue = futureCurrentlyDue.split('|').filter(Boolean);
        } else if (!Array.isArray(futureCurrentlyDue)) {
          futureCurrentlyDue = [];
        }

        this.$set(account, 'pendenciasLink', data.links?.pending_issues || null);
        this.$set(account, 'fullFormLink', data.links?.full_form || null);
        this.$set(account, 'hasAnyPending', !!data.has_any_pending);
        this.$set(account, 'futureCurrentlyDue', futureCurrentlyDue);
        this.$set(
          account,
          'futureDeadlineFormatted',
          data.future_deadline
            ? this.$moment(data.future_deadline).format('DD/MM/YYYY')
            : null
        );
        this.$set(account, 'requiresStripeHostedFlow', !!data.requires_stripe_hosted_flow);
      } catch (error) {
        console.error("Erro ao buscar link de pendências:", error);
      }
    },
    copyPendenciasLink(link) {
      navigator.clipboard.writeText(link);
      this.$eventBus.$emit("showToast", {
        title: "Copiado",
        msg: "Link copiado para a área de transferência",
      });
    },
    getStatusClass(enabled) {
      return enabled ? "badge bg-success" : "badge bg-secondary";
    },
    getStripeLink(accountId) {
      const baseUrl =
        process.env.VUE_APP_ENV === "prod"
          ? "https://dashboard.stripe.com"
          : "https://dashboard.stripe.com/test";
      return `${baseUrl}/connect/accounts/${accountId}`;
    },
    getPayoutStripeLink(connectAccountId, payoutId) {
      const platformAcctId = 'acct_1Mo623D1MBNGVeyV';
      const urlMid = process.env.VUE_APP_ENV === 'prod' ? 'payouts' : 'test/payouts';
      return `https://dashboard.stripe.com/${platformAcctId}/connect/view-as/${connectAccountId}/${urlMid}/${payoutId}`;
    },
    getBusinessTypeLabel(type) {
      if (type === "individual") return "Pessoa Física";
      if (type === "company") return "Empresa";
      return type;
    },
    getAccountName(account) {
      if (account.business_type === "individual") {
        return (
          `${account.individual_first_name || ""} ${
            account.individual_last_name || ""
          }`.trim() || "-"
        );
      }
      if (account.business_type === "company") {
        return account.company_name || "-";
      }
      return "-";
    },
    isPdf(url) {
      if (!url) return false;
      return url.toLowerCase().endsWith('.pdf');
    },
    getDocumentUrl(path) {
      if (!path) return '';
      return this.$baseImageUrl + path;
    },
    formatCurrency(value) {
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    getBalanceClass(value) {
      const num = parseFloat(value);
      if (isNaN(num) || num === 0) return 'text-muted';
      if (num < 0) return 'text-danger';
      return 'text-success';
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      return this.$moment(dateStr).format('DD/MM/YYYY HH:mm');
    },
    getFailureMessageTranslated(failureCode) {
      if (!failureCode) return '-';
      const translated = this.$userService.payoutService.getFailureMessage(failureCode);
      return translated || failureCode;
    },
    closeResetModal() {
      this.showResetModal = false;
      this.confirmReset = false;
    },
    async handleResetStripe() {
      if (!this.item?.id) return;

      this.resetting = true;
      try {
        await this.$userService.network.post(
          `api/users/stripe-connect-reset/${this.item.id}`
        );

        this.$eventBus.$emit("showToast", {
          title: "Sucesso",
          msg: "Conta Stripe Connect resetada com sucesso. O psicólogo foi deslogado.",
        });

        this.closeResetModal();
        this.loadConnectAccounts();
      } catch (error) {
        console.error("Erro ao resetar conta Stripe:", error);
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: error.message || "Erro ao resetar conta Stripe Connect",
        });
      } finally {
        this.resetting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Card principal */
.connect-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

/* Banner de conta antiga */
.old-account-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 2px solid #dc2626;
  border-left: 8px solid #dc2626;
  border-radius: 8px;
  color: #7f1d1d;
}

.old-account-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #dc2626;
  color: #fff;
  border-radius: 50%;
  font-size: 1.5rem;
}

.old-account-content {
  flex-grow: 1;
}

.old-account-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #991b1b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

/* Card ativo - destaque visual */
.connect-card-active-healthy,
.connect-card-active-warning {
  position: relative;
}

.connect-card-active-healthy::before,
.connect-card-active-warning::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  border-radius: 4px 0 0 4px;
}

/* Variação saudável (verde) */
.connect-card-active-healthy {
  border: 2px solid #198754;
  box-shadow: 0 4px 16px rgba(25, 135, 84, 0.18);
}

.connect-card-active-healthy::before {
  background: linear-gradient(180deg, #198754 0%, #20c997 100%);
}

.connect-card-active-healthy .card-header {
  background-color: #f0fdf4;
  border-bottom-color: #bbf7d0;
}

/* Variação com pendências (laranja) */
.connect-card-active-warning {
  border: 2px solid #fd7e14;
  box-shadow: 0 4px 16px rgba(253, 126, 20, 0.2);
}

.connect-card-active-warning::before {
  background: linear-gradient(180deg, #fd7e14 0%, #ffc107 100%);
}

.connect-card-active-warning .card-header {
  background-color: #fff7ed;
  border-bottom-color: #fed7aa;
}

/* Variação com pendência futura (amarelo/azul, não bloqueia ainda) */
.connect-card-active-future {
  border: 2px solid #0dcaf0;
  box-shadow: 0 4px 16px rgba(13, 202, 240, 0.18);
}

.connect-card-active-future::before {
  background: linear-gradient(180deg, #0dcaf0 0%, #6edff6 100%);
}

.connect-card-active-future .card-header {
  background-color: #f0fbfd;
  border-bottom-color: #b6effa;
}

/* Seções de informação */
.info-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Tabela de informações */
.info-table {
  width: 100%;
  font-size: 0.9rem;
}

.info-table tr {
  border-bottom: 1px solid #e9ecef;
}

.info-table tr:last-child {
  border-bottom: none;
}

.info-table td {
  padding: 8px 0;
}

.info-table td.label {
  color: #6c757d;
  width: 100px;
}

/* Status badges */
.status-ok {
  color: #198754;
  font-weight: 500;
}

.status-ok i {
  margin-right: 4px;
}

.status-off {
  color: #6c757d;
}

.status-off i {
  margin-right: 4px;
}

/* Alertas */
.alerts-section {
  border-top: 1px solid #e9ecef;
  padding-top: 16px;
}

.alert-box {
  border-radius: 8px;
  padding: 16px;
}

.alert-box.alert-danger {
  background-color: #fff5f5;
  border-left: 4px solid #dc3545;
}

.alert-box.alert-warning {
  background-color: #fffdf5;
  border-left: 4px solid #ffc107;
}

.alert-box.alert-info {
  background-color: #f0fbfd;
  border-left: 4px solid #0dcaf0;
}

.alert-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.alert-box.alert-danger .alert-header i {
  color: #dc3545;
}

.alert-box.alert-warning .alert-header i {
  color: #ffc107;
}

.alert-box.alert-info .alert-header i {
  color: #0dcaf0;
}

.stripe-hosted-note {
  font-size: 0.8rem;
  color: #997404;
  background-color: #fff8e1;
  border-radius: 6px;
  padding: 6px 10px;
}

.alert-content {
  padding-left: 24px;
}

/* Lista de pendências */
.pendencias-list {
  padding-left: 20px;
  margin: 0;
  columns: 2;
  column-gap: 32px;
}

.pendencias-list li {
  color: #6c757d;
  font-size: 0.875rem;
  padding: 4px 0;
  break-inside: avoid;
}

/* Documentos */
.doc-item {
  text-align: center;
}

.doc-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.15s;
}

.doc-link:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
}

.doc-preview {
  max-width: 120px;
  max-height: 80px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  object-fit: cover;
  transition: transform 0.15s, box-shadow 0.15s;
}

.doc-preview:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
